import { UserId } from './prebidConfig'
import { Site } from './site'

export type PrebidModuleType = 'RTD' | 'Identity' | 'Analytics' | 'Rendering'
type BaseParam = {
  label: string
  sublabel: string
  disabled?: boolean
  // a param is required by default: the module cannot be enabled until it is filled
  optional?: boolean
}

/**
 * Describes one entry of a module form. `objectList` and `oneOf` nest: their own fields are described the same way,
 * which is how a prebid config as deep as pubProvidedId's eids is expressed without code.
 * Keys of `fields` may be dotted (`ext.stype`) to reach into a nested object.
 *
 * - `objectList` renders a repeatable group.
 * - `oneOf` renders mutually exclusive variants of the same parent object: picking one drops the other's keys from
 *   the saved config, so prebid never receives both.
 */
export type Param =
  | (BaseParam & { type: 'string' | 'number' | 'array' | 'boolean' | 'json' })
  | (BaseParam & { type: 'select'; options: string[] })
  | (BaseParam & { type: 'objectList'; itemLabel: string; fields: Record<string, Param> })
  | (BaseParam & { type: 'oneOf'; variants: Record<string, { label: string; fields: Record<string, Param> }> })
export type PrebidModule = {
  code: string
  displayName: string
  /**
   * One line on what the module does, shown under its name on the Modules page.
   *
   * Optional because it is data rather than structure: the backfill only writes a line for the modules
   * prebid.org still documents, and a card with no tagline simply omits it — better than a sentence
   * invented here and indistinguishable from a sourced one.
   */
  tagline?: string
  type: PrebidModuleType
  formParams: Record<string, Param>
  defaultValues: Omit<PrebidModuleData<PrebidModuleType>, 'name'> & Record<string, unknown>
  modulePbjsName: string
  pbjsMinRequiredVersion?: string
}

// A prebid module catalog entry as stored in the adm_module_catalog collection / returned by the API.
export type PrebidModuleCatalogEntry = PrebidModule & { id: string }

// TODO 2024-09-16 SLE: see how to avoid this Bob Object
export type PrebidModuleData<Type extends PrebidModuleType> = {
  name: string
  params?: Type extends 'RTD' | 'Identity' ? Record<string, unknown> : never
  storage?: Type extends 'RTD' ? Record<string, unknown> : never
  options?: Type extends 'Analytics' ? Record<string, unknown> : never
  waitForIt?: Type extends 'RTD' ? boolean : never
}

export type PrebidModuleDataBySite<Type extends PrebidModuleType> = {
  siteId: Site['id']
  label: Site['name'] | string
  moduleData?: Omit<PrebidModuleData<Type>, 'name' | 'provider'>
  enabled: boolean
}

export type MultipleEditPayload<Type extends PrebidModuleType> = {
  enabled: boolean
  selectedSites: Site['id'][]
  moduleData: Type extends 'Identity' ? UserId : PrebidModuleData<Type>
}

export type FullPrebidModule = {
  enabled: boolean
  config: PrebidModuleData<PrebidModuleType>
}

export const EMPTY_PREBID_MODULE: PrebidModule = {
  code: '',
  displayName: '',
  formParams: {},
  defaultValues: {},
  modulePbjsName: '',
  type: 'Identity',
}
